/**
 * /admin/team — the professional's own team (professional-scoped members).
 * @file src/web/features/admin/pages/team.tsx
 *
 * Professional admin manages their professional's logins: add Professional Admin or
 * Professional Moderator, edit role, activate/deactivate, reset password
 * (revealed once), and remove. The professional owner is protected. Scoped to the
 * caller's tenant server-side.
 */

import { useEffect, useState } from 'react';
import {
  Plus, X, Loader2, CheckCircle2, ShieldAlert, KeyRound, Copy, Download, Check,
  Trash2, Pencil, UserCheck, UserX, ShieldCheck,
} from 'lucide-react';
import { SEO } from '../../../shared/components';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { SettingsTabs } from '../components/SettingsTabs';
import { PageLoading } from '../components/PageLoading';
import { adminFetch, adminFetchJson } from '../lib/admin-api';

interface Member {
  id: string; name: string | null; email: string; roleLevel: string; roleLabel: string;
  isActive: boolean; lastLogin: string | null; isOwner: boolean;
}

const ROLES = [
  { value: 'admin.tenant', label: 'Professional Admin' },
  { value: 'moderator.manage', label: 'Professional Moderator' },
];
const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

export default function TeamPage() {
  const [data, setData] = useState<{ items: Member[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const [reveal, setReveal] = useState<null | { name: string; email: string; tempPassword: string }>(null);
  const [confirmDelete, setConfirmDelete] = useState<Member | null>(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    adminFetchJson<{ items: Member[] }>('/api/team')
      .then((d) => !cancel && setData(d))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const toggleActive = async (m: Member) => {
    await adminFetch(`/api/team/${m.id}/${m.isActive ? 'deactivate' : 'activate'}`, { method: 'POST' });
    refresh();
  };
  const doReset = async (m: Member) => {
    const res = await adminFetchJson<{ email: string; name: string; tempPassword: string }>(`/api/team/${m.id}/reset-password`, { method: 'POST' });
    setReveal({ name: res.name, email: res.email, tempPassword: res.tempPassword });
  };
  const doDelete = async () => {
    if (!confirmDelete) return;
    await adminFetch(`/api/team/${confirmDelete.id}`, { method: 'DELETE' });
    setConfirmDelete(null); refresh();
  };

  return (
    <>
      <SEO title="Team" />
      <AdminPageHeader title="Team" breadcrumbs={[{ label: 'oddits' }, { label: 'Settings', href: '/admin/settings' }, { label: 'Team' }]} />
      <SettingsTabs />

      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="text-sm text-ink-500">People who can sign in to your professional.</p>
        <button onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5">
          <Plus className="h-4 w-4" /> Add member
        </button>
      </div>

      {loading && !data ? (
        <PageLoading label="Loading team" />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-paper">
          <table className="w-full min-w-[40rem] text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-left text-xs uppercase tracking-wide text-ink-400">
                <th className="px-5 py-3 font-medium">Member</th>
                <th className="px-5 py-3 font-medium">Role</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Last login</th>
                <th className="px-5 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {data?.items.map((m) => (
                <tr key={m.id} className="hover:bg-ink-50/60">
                  <td className="px-5 py-3">
                    <div className="font-medium text-ink-900">{m.name || m.email}</div>
                    <div className="text-xs text-ink-400">{m.email}{m.isOwner && ' · owner'}</div>
                  </td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-1 text-ink-600"><ShieldCheck className="h-3.5 w-3.5 text-ink-400" /> {m.roleLabel}</span>
                  </td>
                  <td className="px-5 py-3">
                    {m.isActive
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-ok-50 px-2.5 py-1 text-xs font-medium text-ok-600"><CheckCircle2 className="h-3.5 w-3.5" /> Active</span>
                      : <span className="rounded-full bg-ink-100 px-2.5 py-1 text-xs font-medium text-ink-500">Disabled</span>}
                  </td>
                  <td className="px-5 py-3 text-ink-500">{fmtDate(m.lastLogin)}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <IconBtn title="Reset password" onClick={() => doReset(m)}><KeyRound className="h-4 w-4" /></IconBtn>
                      {!m.isOwner && <IconBtn title="Edit role" onClick={() => setEditing(m)}><Pencil className="h-4 w-4" /></IconBtn>}
                      {!m.isOwner && <IconBtn title={m.isActive ? 'Deactivate' : 'Activate'} onClick={() => toggleActive(m)}>{m.isActive ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}</IconBtn>}
                      {!m.isOwner && <IconBtn title="Remove" danger onClick={() => setConfirmDelete(m)}><Trash2 className="h-4 w-4" /></IconBtn>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <MemberModal onClose={() => setCreating(false)} onCreated={(c) => { setCreating(false); refresh(); if (c) setReveal(c); }} />}
      {editing && <EditRoleModal member={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
      {reveal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-ink-900/40" onClick={() => setReveal(null)} />
          <div className="relative z-10 w-full max-w-md rounded-2xl border border-ink-200 bg-paper p-7 shadow-xl">
            <CredentialReveal creds={[{ role: 'Login', email: reveal.email, tempPassword: reveal.tempPassword }]} title="Member credentials" onDone={() => setReveal(null)} />
          </div>
        </div>
      )}
      {confirmDelete && (
        <ConfirmModal
          title="Remove member" confirmLabel="Remove" name={confirmDelete.name || confirmDelete.email}
          body={<>This removes <strong>{confirmDelete.name || confirmDelete.email}</strong>'s login. This cannot be undone.</>}
          onConfirm={doDelete} onClose={() => setConfirmDelete(null)}
        />
      )}
    </>
  );
}

function IconBtn({ children, title, danger, onClick }: { children: React.ReactNode; title: string; danger?: boolean; onClick: () => void }) {
  return (
    <button title={title} onClick={onClick}
      className={`grid size-8 place-items-center rounded-lg transition-colors ${danger ? 'text-ink-400 hover:bg-danger-50 hover:text-danger-600' : 'text-ink-400 hover:bg-ink-100 hover:text-ink-700'}`}>
      {children}
    </button>
  );
}

function MemberModal({ onClose, onCreated }: { onClose: () => void; onCreated: (c: { name: string; email: string; tempPassword: string } | null) => void }) {
  const [form, setForm] = useState({ name: '', email: '', role: 'moderator.manage' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null); setSaving(true);
    try {
      const res = await adminFetchJson<{ tempPassword: string }>('/api/team', { method: 'POST', body: JSON.stringify(form) });
      onCreated({ name: form.name || form.email, email: form.email.trim().toLowerCase(), tempPassword: res.tempPassword });
    } catch (e: any) {
      setError(e.message?.replace(/^\d+\s/, '') || 'Something went wrong'); setSaving(false);
    }
  };

  return (
    <ModalShell title="Add member" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <div className="rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-2.5 text-sm text-danger-700">{error}</div>}
        <Field label="Name" value={form.name} onChange={(v) => set('name', v)} />
        <Field label="Email (login)" type="email" value={form.email} onChange={(v) => set('email', v)} required />
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-ink-700">Role</label>
          <select value={form.role} onChange={(e) => set('role', e.target.value)}
            className="w-full rounded-xl border border-ink-200 bg-paper px-4 py-3 text-sm focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter">
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <div className="flex items-start gap-2 rounded-xl bg-ink-50 px-3.5 py-3 text-xs text-ink-500">
          <KeyRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-400" />
          <p>A temporary password is generated and shown once. Share it with the member to sign in.</p>
        </div>
        <ModalActions saving={saving} submitLabel="Add member" onClose={onClose} />
      </form>
    </ModalShell>
  );
}

function EditRoleModal({ member, onClose, onSaved }: { member: Member; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(member.name ?? '');
  const [role, setRole] = useState(member.roleLevel);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null); setSaving(true);
    try {
      await adminFetchJson(`/api/team/${member.id}`, { method: 'PATCH', body: JSON.stringify({ name, role }) });
      onSaved();
    } catch (e: any) { setError(e.message?.replace(/^\d+\s/, '') || 'Could not save'); setSaving(false); }
  };

  return (
    <ModalShell title="Edit member" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <div className="rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-2.5 text-sm text-danger-700">{error}</div>}
        <Field label="Name" value={name} onChange={setName} />
        <Field label="Email" value={member.email} onChange={() => {}} disabled />
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-ink-700">Role</label>
          <select value={role} onChange={(e) => setRole(e.target.value)}
            className="w-full rounded-xl border border-ink-200 bg-paper px-4 py-3 text-sm focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter">
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <ModalActions saving={saving} submitLabel="Save changes" onClose={onClose} />
      </form>
    </ModalShell>
  );
}

/* ── shared bits ───────────────────────────────────────────────── */

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-900/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-ink-200 bg-paper p-7 shadow-xl">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-ink-900">{title}</h3>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ saving, submitLabel, onClose }: { saving: boolean; submitLabel: string; onClose: () => void }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" onClick={onClose} className="rounded-full border border-ink-300 bg-paper px-5 py-2.5 text-sm font-semibold text-ink-700 hover:bg-ink-50">Cancel</button>
      <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
        {saving && <Loader2 className="h-4 w-4 animate-spin" />} {submitLabel}
      </button>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required, disabled, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean; disabled?: boolean; placeholder?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-ink-700">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} disabled={disabled} placeholder={placeholder}
        className="w-full rounded-xl border border-ink-200 bg-paper px-4 py-3 text-sm text-ink-900 placeholder:text-ink-400 focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter disabled:bg-ink-50 disabled:text-ink-400" />
    </div>
  );
}

function ConfirmModal({ title, confirmLabel, name, body, onConfirm, onClose }: {
  title: string; confirmLabel: string; name: string; body: React.ReactNode; onConfirm: () => Promise<void>; onClose: () => void;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ok = text.trim() === name;
  const go = async () => {
    if (!ok || busy) return;
    setBusy(true); setError(null);
    try { await onConfirm(); } catch (e: any) { setError(e.message?.replace(/^\d+\s/, '') || 'Something went wrong'); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-900/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-ink-200 bg-paper p-7 shadow-xl">
        <div className="mb-4 flex items-center gap-3">
          <span className="grid size-10 place-items-center rounded-xl bg-danger-50 text-danger-600"><Trash2 className="h-5 w-5" /></span>
          <h3 className="text-lg font-semibold text-ink-900">{title}</h3>
        </div>
        <p className="text-sm text-ink-600">{body}</p>
        {error && <div className="mt-3 rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-2.5 text-sm text-danger-700">{error}</div>}
        <label className="mt-4 block text-sm text-ink-600">Type <span className="font-semibold text-ink-900">{name}</span> to confirm</label>
        <input autoFocus value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') go(); }} placeholder={name}
          style={{ outline: 'none', boxShadow: 'none' }}
          className="mt-1.5 w-full rounded-xl border border-ink-200 bg-paper px-4 py-2.5 text-sm text-ink-900 placeholder:text-ink-400 focus:border-ink-300 focus-visible:outline-none" />
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="rounded-full border border-ink-300 bg-paper px-5 py-2.5 text-sm font-semibold text-ink-700 hover:bg-ink-50">Cancel</button>
          <button onClick={go} disabled={!ok || busy} className="inline-flex items-center gap-2 rounded-full bg-danger-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-danger-700 disabled:cursor-not-allowed disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function CredentialReveal({ creds, title, onDone }: { creds: Array<{ role: string; email: string; tempPassword: string }>; title: string; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const loginUrl = `${window.location.origin}/login`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(creds.map((c) => `${c.role}\nEmail: ${c.email}\nPassword: ${c.tempPassword}`).join('\n\n')); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  };
  const download = () => {
    const body = `oddits — login credentials\n==========================\nLogin URL: ${loginUrl}\n\n` +
      creds.map((c) => `${c.role}\n Email : ${c.email}\n Password: ${c.tempPassword}`).join('\n\n') +
      `\n\nPlease sign in and change the password on first login.\n`;
    const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }));
    const a = document.createElement('a'); a.href = url; a.download = 'oddits-credentials.txt'; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <span className="grid size-10 place-items-center rounded-xl bg-ok-50 text-ok-600"><CheckCircle2 className="h-5 w-5" /></span>
        <h3 className="text-lg font-semibold text-ink-900">{title}</h3>
      </div>
      <p className="mb-3 flex items-start gap-2 rounded-xl border border-warn-200 bg-warn-50 px-3.5 py-2.5 text-xs text-warn-700">
        <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Save these now — passwords are shown only once.
      </p>
      <div className="space-y-3">
        {creds.map((c) => (
          <div key={c.email} className="rounded-xl border border-ink-200 bg-ink-50 p-4 text-sm">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">{c.role}</p>
            <div className="flex items-center gap-3"><span className="w-20 shrink-0 text-ink-500">Email</span><span className="min-w-0 flex-1 break-all font-medium text-ink-800">{c.email}</span></div>
            <div className="mt-1 flex items-center gap-3"><span className="w-20 shrink-0 text-ink-500">Password</span><span className="min-w-0 flex-1 break-all font-mono font-semibold text-ink-900">{c.tempPassword}</span></div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={copy} className="inline-flex items-center gap-2 rounded-full border border-ink-300 bg-paper px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50">{copied ? <Check className="h-4 w-4 text-ok-600" /> : <Copy className="h-4 w-4" />} {copied ? 'Copied' : 'Copy'}</button>
        <button onClick={download} className="inline-flex items-center gap-2 rounded-full border border-ink-300 bg-paper px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50"><Download className="h-4 w-4" /> Download</button>
        <button onClick={onDone} className="ml-auto rounded-full bg-inkblack px-5 py-2 text-sm font-semibold text-white">Done</button>
      </div>
    </div>
  );
}
